import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useProducts } from './useProducts';
import { formatDisplayTitle, formatProductTitle } from './formatProductTitle';
import { productMatchesSearchQuery } from './productSearchUtils';
import { productDetailPath } from './productPaths';
import { hrefForSameSiteAsset, productGalleryImagePaths } from './productMediaUtils';
import AddToCartButton from './cart/AddToCartButton';
import ProductPrice from './catalog/ProductPrice';
import ProductRefId from './catalog/ProductRefId';
import { productEffectivePrice } from './productPricing';
import { isOutOfStock, normalizeStockQty } from './cart/cartUtils';
import { productCharPersonalizationEnabled } from './productPersonalization';
import { useSiteSettings } from './SiteSettingsContext';
import { storeSeo } from './siteBrand';

export default function ProductSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = String(searchParams.get('q') || '').trim();
  const [draft, setDraft] = useState(qParam);

  const { allProducts, categories, loading } = useProducts();
  const { settings: site } = useSiteSettings();
  const seo = storeSeo(site, 'seo_search_title', 'Search products', 'Search our handcrafted catalog by product name or ID.');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setDraft(qParam);
  }, [qParam]);

  const [inStockOnly, setInStockOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');

  const results = useMemo(() => {
    if (!qParam) return [];
    let list = allProducts.filter((p) => productMatchesSearchQuery(p, qParam));
    if (inStockOnly) list = list.filter((p) => !isOutOfStock(normalizeStockQty(p.stock_qty)));
    if (categoryFilter) list = list.filter((p) => p.category === categoryFilter);
    return list;
  }, [allProducts, qParam, inStockOnly, categoryFilter]);

  const categoryName = (slug) => {
    const c = categories.find((x) => x.slug === slug);
    return c ? formatDisplayTitle(c.name || slug) : formatDisplayTitle(String(slug || '').replace(/-/g, ' '));
  };

  const submit = (e) => {
    e.preventDefault();
    const q = draft.trim();
    if (q) setSearchParams({ q });
    else setSearchParams({});
  };

  return (
    <div className="store-page storefront-search-page">
      <div className="container store-page__container py-4 py-lg-5">
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
      </Helmet>

      <div className="storefront-page-intro mb-4">
        <h1 className="h2 mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--deep-green)' }}>
          Search the catalog
        </h1>
        <p className="text-muted mb-0">Find pieces by name, product ID, or keywords.</p>
      </div>

      <form className="mb-4 shop-hero-search" onSubmit={submit} role="search">
        <label htmlFor="site-product-search" className="visually-hidden">
          Search catalog
        </label>
        <div className="input-group input-group-lg shadow-sm">
          <input
            id="site-product-search"
            type="search"
            className="form-control"
            placeholder="Product name or ID…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoComplete="off"
          />
          <button type="submit" className="btn btn-primary px-4">
            Search
          </button>
        </div>
      </form>

      {qParam ? (
        <div className="d-flex flex-wrap gap-2 align-items-center mb-3 product-listing-toolbar__controls">
          <label className="form-check form-switch store-switch--inline product-listing-toolbar__switch mb-0">
            <input
              type="checkbox"
              role="switch"
              className="form-check-input"
              id="search-in-stock-only"
              checked={inStockOnly}
              onChange={(e) => setInStockOnly(e.target.checked)}
            />
            <span className="form-check-label">In stock only</span>
          </label>
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: '12rem', height: '2.125rem', minHeight: '2.125rem' }}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {categoryName(c.slug)}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {loading && <p className="text-muted">Loading catalog…</p>}

      {!loading && !qParam && <p className="text-muted">Enter a search term above.</p>}

      {!loading && qParam && results.length === 0 && (
        <p className="text-muted">
          No products matched <strong>{qParam}</strong>. Try another name, ID, or keyword.
        </p>
      )}

      {!loading && qParam && results.length > 0 && (
        <>
          <p className="small text-muted mb-3">
            {results.length} result{results.length === 1 ? '' : 's'} for <strong>{qParam}</strong>
          </p>
          <ul className="list-unstyled search-results-list">
            {results.map((p) => {
              const paths = productGalleryImagePaths(p);
              const thumb = paths[0] ? hrefForSameSiteAsset(paths[0]) : '';
              const to = productDetailPath(p);
              const price = productEffectivePrice(p);
              const oos = isOutOfStock(normalizeStockQty(p.stock_qty));
              return (
                <li key={`${p.category}-${p.id}`} className="search-results-list__item">
                  <Link to={to} className="search-results-list__media text-decoration-none">
                    {thumb ? (
                      <img src={thumb} alt="" className="search-results-list__thumb" loading="lazy" />
                    ) : (
                      <span className="search-results-list__thumb search-results-list__thumb--empty" aria-hidden />
                    )}
                  </Link>
                  <div className="search-results-list__body min-w-0 flex-grow-1">
                    <Link to={to} className="search-results-list__title text-decoration-none">
                      {formatProductTitle(p.name)}
                      {productCharPersonalizationEnabled(p) ? (
                        <span className="search-results-list__monogram-tag">✦ Monogram</span>
                      ) : null}
                    </Link>
                    <p className="search-results-list__meta small text-muted mb-1">
                      {categoryName(p.category)} · <ProductRefId id={p.id} as="span" className="product-id product-id--inline" />
                      {oos ? ' · Out of stock' : ''}
                    </p>
                    <p className="search-results-list__price fw-semibold mb-2 mb-md-2">
                      <ProductPrice product={p} showBadge={false} />
                    </p>
                    {price > 0 && !oos ? (
                      <AddToCartButton product={p} categorySlug={p.category} compact />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
      </div>
    </div>
  );
}
